import { useFrame, useThree } from '@react-three/fiber';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Group, OrthographicCamera } from 'three';
import { useGetRaycastFromCamera } from '../../hooks/general';
import { getScreenContentIdFromSceneId } from '../../utils';
import { ITransCatTypes, useDataStore, useSceneStore } from '../../zustand';
import withButtonBehaviour from '../hoc/withButtonBehaviour';
import withImageBehaviour from '../hoc/withImageBehaviour';
import withModel3dBehaviour from '../hoc/withModel3dBehaviour';
import withTextBehaviour from '../hoc/withTextBehaviour';
import withVideoBehaviour from '../hoc/withVideoBehaviour';
import { Button, Image, Model3d, Text, Text3d, Video } from '@r3f/index';
import { IComponentType, IScreenAnchorGroup, IScreenAnchorPositionType, IScreenContent, ITrackingTypes } from '@r3f-component-data-structure';
import { calulateScreenRelativeNormalizationFactor, getSnapshotButtonId } from '@r3f/utils/general';
import { getAnchorGroupPrositionDict } from '@r3f/utils/screen-relative';
import withText3dBehaviour from '../hoc/withText3dBehaviour';
import { useGetActiveTrackingType } from '../hoc/hocCustomHooks';
import { TOP_SAFE_SPACE_IN_PIXELS } from '@/constants';
import { Jute } from '@/index';
import { IScreenRelativeScaleType } from '@r3f-component-data-structure/types/screenRelative';
import { useScreenRelativeOverflow } from '../../hooks/useScreenRelativeOverflow';

const ScreenRelativeImageHOC = memo(withImageBehaviour(Image));
ScreenRelativeImageHOC.displayName = 'Screen_Relative_Image_HOC'; // helps with identification in debugger
const ScreenRelativeButtonHOC = memo(withButtonBehaviour(Button));
ScreenRelativeButtonHOC.displayName = 'Screen_Relative_Button_HOC';
const ScreenRelativeVideoHOC = memo(withVideoBehaviour(Video));
ScreenRelativeVideoHOC.displayName = 'Screen_Relative_Video_HOC';
const ScreenRelativeTextHOC = memo(withTextBehaviour(Text));
ScreenRelativeTextHOC.displayName = 'Screen_Relative_Text_HOC';
const ScreenRelativeText3dHOC = memo(withText3dBehaviour(Text3d));
ScreenRelativeText3dHOC.displayName = 'Screen_Relative_Text3d_HOC'; 
const ScreenRelativeModel3dHOC = memo(withModel3dBehaviour(Model3d));
ScreenRelativeModel3dHOC.displayName = 'Screen_Relative_Model3d_HOC';

interface IParentProps {
	cameraRef: React.RefObject<OrthographicCamera>;
	sceneId: string;
}

const AnchorGroups: React.FunctionComponent<IParentProps> = ({ sceneId, cameraRef }) => {
	const { size, scene } = useThree();
	const activeSceneId = useSceneStore(({ activeSceneId }) => activeSceneId);
	const componentsById = useDataStore((state) => state.componentsById);
	const screenRelativeScaleType = useDataStore((state) => state.screenRelativeScaleType) ?? 'height-relative' as IScreenRelativeScaleType;
	const screenContentId = getScreenContentIdFromSceneId(sceneId, componentsById);
	const [screenAspectRatio, setScreenAspectRatio] = useState(size.width / size.height);
	const snapshotButtonId = getSnapshotButtonId(componentsById, activeSceneId);
	const transitionCategory = useSceneStore((state) => state.transitionCategory[sceneId]);
	const raycast = useGetRaycastFromCamera(cameraRef);
	const activeTrackingType = useGetActiveTrackingType();

	const isFaceTrackingActive = activeTrackingType === ITrackingTypes.face;

	const getAnchorGroupPosition = (anchorPositionType: IScreenAnchorPositionType, isFaceTrackingActive: boolean) => {
		const position = anchorGroupPositionDict[anchorPositionType];

		const safeSpaceAdjustmentRequired = (
			anchorPositionType !== IScreenAnchorPositionType.leftBottom && 
			anchorPositionType !== IScreenAnchorPositionType.rightBottom && 
			anchorPositionType !== IScreenAnchorPositionType.bottomMiddle && 
			((!isFaceTrackingActive && Jute.isMobileWebXRExperience) || Jute.isAppClip)
		) ;

		if (safeSpaceAdjustmentRequired) {	
			const orthCam = scene.getObjectByName('screenRelativeCamera') as OrthographicCamera;
			const pixelsPerCoord = (size.height / (orthCam.top - orthCam.bottom)) * orthCam.zoom;
			const topSafeSpaceCoords = TOP_SAFE_SPACE_IN_PIXELS / pixelsPerCoord;
			position[1] = position[1] - topSafeSpaceCoords;
		}

		return position;
	}
	
	useFrame(({ size }) => {
		const newRatio = size.width / size.height;
		if (newRatio !== screenAspectRatio) setScreenAspectRatio(newRatio);
	});

	const anchorGroupPositionDict = getAnchorGroupPrositionDict(size);
	const normalizationFactor = calulateScreenRelativeNormalizationFactor(screenRelativeScaleType, size.height);
	const screenContent = componentsById?.[screenContentId || ''] as IScreenContent | undefined;

	// The live anchor groups, so the overflow watcher can measure against their world matrices. A ref map
	// rather than `scene.getObjectByName`: every scene renders its own AnchorGroups and names the groups
	// after the anchor position, so those names are NOT unique across scenes and a lookup by name would
	// return an arbitrary scene's group.
	const anchorGroupsRef = useRef(new Map<IScreenAnchorPositionType, Group>());
	const setAnchorGroupRef = useCallback(
		(anchorPositionType: IScreenAnchorPositionType) => (group: Group | null) => {
			if (group) anchorGroupsRef.current.set(anchorPositionType, group);
			else anchorGroupsRef.current.delete(anchorPositionType);
		},
		[]
	);

	const entityIdsByAnchor = useMemo(() => {
		const dict: Partial<Record<IScreenAnchorPositionType, string[]>> = {};
		screenContent?.children.forEach((id) => {
			const anchorGroup = componentsById?.[id] as IScreenAnchorGroup | undefined;
			if (!anchorGroup) return;
			dict[anchorGroup.anchorPositionType] = anchorGroup.children.filter((childId) => childId !== snapshotButtonId);
		});
		return dict;
	}, [screenContent, componentsById, snapshotButtonId]);

	useScreenRelativeOverflow({
		sceneId,
		isActiveScene: activeSceneId === sceneId,
		cameraRef,
		groupsRef: anchorGroupsRef,
		entityIdsByAnchor,
	});

	const anchorGroups = useMemo(
		() =>
			screenContent?.children.map((id) => {
			if (transitionCategory === ITransCatTypes.hidden) return null;
				const anchorGroup = componentsById?.[id] as IScreenAnchorGroup;

				const anchorR3fComponents = anchorGroup.children.map((id) => {
					if (id === snapshotButtonId || !componentsById) return null;
					const component = componentsById[id];

					switch (component.type) {
						case IComponentType.Button:
							return <ScreenRelativeButtonHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						case IComponentType.Image:
							return <ScreenRelativeImageHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						case IComponentType.Text:
							return <ScreenRelativeTextHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						case IComponentType.Text3d:
							return <ScreenRelativeText3dHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						case IComponentType.Video:
							return <ScreenRelativeVideoHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						case IComponentType.Model3d:
							return <ScreenRelativeModel3dHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						default:
							return null;
					}
				});

				return (
					<group
						key={anchorGroup.anchorPositionType}
						ref={setAnchorGroupRef(anchorGroup.anchorPositionType)}
						name={anchorGroup.anchorPositionType}
						scale={normalizationFactor}
						position={getAnchorGroupPosition(anchorGroup.anchorPositionType, isFaceTrackingActive)}
						visible={activeSceneId === sceneId}
					>
						{anchorR3fComponents}
					</group>
				);
			}),
		// `componentsById` and `sceneId` are what make this list react to a live content update. Without
		// them, adding, deleting or re-anchoring screen-relative content was silently ignored until one of
		// the runtime deps happened to change. Existing entities still updated, because each HOC subscribes
		// to its own id — so the symptom was specifically that STRUCTURAL edits did nothing. ArComponents
		// does the same job for the AR tree and always had these deps; this was the odd one out.
		// `normalizationFactor` (a number) and `raycast` (stable — from a useState initialiser) are listed
		// for honesty and neither causes an extra recompute.
		//
		// `getAnchorGroupPosition` is deliberately omitted, which is why the disable stays: it is
		// re-created on every render, so listing it would rebuild this whole element tree every render and
		// defeat the memo. Everything it reads is either `size`-derived or `isFaceTrackingActive`, and the
		// latter is already a dep. `setAnchorGroupRef` is omitted for the opposite reason — it is
		// `useCallback([])`-stable, so listing it would change nothing.
		[componentsById, sceneId, screenAspectRatio, activeSceneId, snapshotButtonId, transitionCategory, isFaceTrackingActive, normalizationFactor, raycast]
	); // eslint-disable-line react-hooks/exhaustive-deps

	return <>{anchorGroups || null}</>;
};

export default AnchorGroups;
